<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import CustomerEmptyState from '@/V2/customers/CustomerEmptyState.vue';
import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iKeyIcon,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
} from '@inscribe/signal';
import {
  COMPLIANCE_CHECK_TYPE_TO_ICON,
  COMPLIANCE_RESULT_TO_READABLE_LABEL,
  COMPLIANCE_RESULT_TO_STATUS,
  COMPLIANCE_ENTITY_TYPE,
  COMPLIANCE_COMPANY_CHECK_TYPE,
} from '@/constants/businessComplianceAgent';
import { CHECK_INFO } from '@/constants/businessComplianceAgent';
import { Statuses } from '@/V2/components/status/utils';
import { RUN_STATUS } from '@/constants/analyst-constants';
import CustomerListLoadingBars from '@/V2/customers/CustomerListLoadingBars.vue';

export default {
  components: {
    ExpandableContainer,
    iButton,
    iKeyIcon,
    ExpandableContainerItem,
    CustomerEmptyState,
    StatusBadge,
    CustomerListLoadingBars,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    latestComplianceRun: {
      type: Object,
      default: () => {},
    },
    complianceStatus: {
      type: String,
      default: '',
    },
    isComplianceRunning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      iKeyIconSize,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
      Statuses,
    };
  },
  computed: {
    companyName() {
      const legalName =
        this.latestComplianceRun?.output?.canonical_profile?.legal_name;
      return legalName?.value || legalName;
    },
    showLoadingState() {
      return this.loading || this.isComplianceRunning;
    },
    showEmptyState() {
      return (
        !this.loading &&
        (!this.latestComplianceRun ||
          this.latestComplianceRun?.output?.company_checks?.length === 0)
      );
    },
    businessChecks() {
      return this.latestComplianceRun?.output?.company_checks
        ?.map((check) => {
          return {
            ...check,
            icon: COMPLIANCE_CHECK_TYPE_TO_ICON[check.type],
            badgeLabel: COMPLIANCE_RESULT_TO_READABLE_LABEL[check.result],
            status: COMPLIANCE_RESULT_TO_STATUS[check.result],
            subtitle:
              CHECK_INFO[COMPLIANCE_ENTITY_TYPE.COMPANY][check.type].subtype,
            hasBadge: true,
          };
        })
        .sort((a, b) => {
          const aIndex = Object.values(COMPLIANCE_COMPANY_CHECK_TYPE).indexOf(
            a.type,
          );
          const bIndex = Object.values(COMPLIANCE_COMPANY_CHECK_TYPE).indexOf(
            b.type,
          );
          return aIndex - bIndex;
        });
    },
    analysisHasRun() {
      return this.complianceStatus === RUN_STATUS.FINISHED;
    },
  },
  methods: {
    onViewProfileButtonClick() {
      this.$router.push({
        name: 'customer-profile',
        params: {
          customerId: this.$route.params.customerId,
        },
      });
    },
    onCheckClick(check) {
      this.$emit('show-check-details', check);
    },
    getSubtype(check) {
      const checkInfoForType =
        CHECK_INFO[COMPLIANCE_ENTITY_TYPE.COMPANY][check.type];
      if (checkInfoForType) {
        return checkInfoForType.subtypes[check.result] || '';
      } else {
        return 'Check completed';
      }
    },
  },
};
</script>

<template>
  <div
    class="compliance-business-checks"
    data-testid="customer-compliance-business-checks"
  >
    <ExpandableContainer
      :title="isComplianceRunning ? 'Business Checks' : companyName"
      :subtitle="isComplianceRunning ? 'Analyzing...' : 'Business Checks'"
    >
      <template #tools>
        <i-button
          label="View Profile"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          @button-click="onViewProfileButtonClick"
        />
      </template>

      <template #items>
        <!-- loading state -->
        <customer-list-loading-bars
          v-if="showLoadingState"
          data-testid="customer-compliance-business-checks-loading"
        >
        </customer-list-loading-bars>

        <!-- empty state -->
        <customer-empty-state
          v-else-if="showEmptyState"
          title="No Compliance Checks"
          :description="
            analysisHasRun
              ? 'Analysis complete. No completed compliance checks'
              : 'Compliance checks will appear here'
          "
          icon="medium_user_single"
          :show-button="false"
          data-testid="customer-fraud-signals-empty-state"
        />

        <!-- checks list -->
        <div v-else class="checks-container d-flex flex-column">
          <expandable-container-item
            v-for="check in businessChecks"
            :key="check.type"
            :title="check.title"
            :subtitle="getSubtype(check)"
            @on-item-click="onCheckClick(check)"
            @on-right-icon-click="onCheckClick(check)"
            class="check-item"
            data-testid="customer-compliance-business-check-item"
          >
            <template #left-container>
              <i-key-icon
                :icon="check.icon"
                :size="iKeyIconSize.MEDIUM"
                :shape="iKeyIconShape.SQUARE"
                :icon-style="iKeyIconStyle.OUTLINE"
                :type="iKeyIconType.NEUTRAL"
              />
            </template>

            <template #right-container>
              <status-badge
                v-if="check.hasBadge"
                :state="check.status"
                :label="check.badgeLabel"
              ></status-badge>
            </template>
          </expandable-container-item>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.compliance-business-checks {
  .checks-container {
    gap: 1px;
  }
}
</style>
